import { z } from 'zod';

import {
  MAX_ADMIN_GPU_BUCKET_SECONDS,
  MAX_ADMIN_TIME_RANGE_POINTS,
  MIN_ADMIN_GPU_BUCKET_SECONDS,
  parseAdminParisCivilDate,
} from './gpu-time-policy';

const ISO_INSTANT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|([+-])(\d{2}):(\d{2}))$/;

/** Refuse explicitement les dates que `new Date()` normaliserait silencieusement. */
function isStrictIsoInstant(value: string): boolean {
  const match = ISO_INSTANT_PATTERN.exec(value);
  if (!match) return false;

  const [, yearRaw, monthRaw, dayRaw, hourRaw, minuteRaw, secondRaw, , , , offsetHourRaw, offsetMinuteRaw] = match;
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  const second = secondRaw === undefined ? 0 : Number(secondRaw);
  const offsetHour = offsetHourRaw === undefined ? 0 : Number(offsetHourRaw);
  const offsetMinute = offsetMinuteRaw === undefined ? 0 : Number(offsetMinuteRaw);

  return parseAdminParisCivilDate(`${yearRaw}-${monthRaw}-${dayRaw}`) !== null
    && hour <= 23
    && minute <= 59
    && second <= 59
    && offsetHour <= 23
    && offsetMinute <= 59
    && Number.isFinite(Date.parse(value));
}

/** Instant ISO avec offset complet et calendrier réel, y compris années bissextiles. */
export const AdminIsoInstantSchema = z.string()
  .trim()
  .refine(isStrictIsoInstant, 'Expected a complete, calendrically valid ISO instant with an offset');

const AdminQueryScalarSchema = z.string().trim().min(1);

/** Query wire de `GET /api/admin/gpu/concurrency`; les arrays Express sont refusés. */
export const AdminGpuConcurrencyQuerySchema = z.object({
  from: AdminIsoInstantSchema.optional(),
  toExclusive: AdminIsoInstantSchema.optional(),
  /** Alias de transition pour les dashboards historiques. */
  to: AdminIsoInstantSchema.optional(),
  bucketSeconds: AdminQueryScalarSchema.optional(),
  timeZone: AdminQueryScalarSchema.optional(),
});

const NonnegativeFiniteNumberSchema = z.number().finite().nonnegative();

/**
 * Vitesse de décodage des requêtes terminées dans la tranche, en tokens/s par
 * requête (output_tokens / (durée − TTFB)). `null` quand aucune requête
 * éligible n'a fini dans la tranche : une tranche vide n'est pas « 0 tk/s ».
 */
const GpuTokensPerSecondSchema = z.object({
  samples: z.number().int().positive(),
  p50: NonnegativeFiniteNumberSchema,
  /** Vitesse que 90 % des requêtes dépassent (quantile 0.1) : queue lente, convention latence. */
  p90: NonnegativeFiniteNumberSchema,
  /** Vitesse que 99 % des requêtes dépassent (quantile 0.01) : les 1 % les plus lentes sont en dessous. */
  p99: NonnegativeFiniteNumberSchema,
}).strict();

const GpuConcurrencyBucketSchema = z.object({
  time: AdminIsoInstantSchema,
  max: NonnegativeFiniteNumberSchema,
  avg: NonnegativeFiniteNumberSchema,
  maxPriority: NonnegativeFiniteNumberSchema,
  avgPriority: NonnegativeFiniteNumberSchema,
  byChain: z.record(NonnegativeFiniteNumberSchema),
  rateLimited429: z.number().int().nonnegative(),
  rateLimited429Priority: z.number().int().nonnegative(),
  rateLimited429Batch: z.number().int().nonnegative(),
  tokensPerSecond: GpuTokensPerSecondSchema.nullable(),
}).strict();

/** Réponse publique staff, validée à l'écriture backend et consommée par l'onglet GPU. */
export const GpuConcurrencyResponseSchema = z.object({
  from: AdminIsoInstantSchema,
  toExclusive: AdminIsoInstantSchema.optional(),
  /** Alias de lecture temporaire, identique à `toExclusive` quand les deux sont servis. */
  to: AdminIsoInstantSchema.optional(),
  bucketSeconds: z.number().int().min(MIN_ADMIN_GPU_BUCKET_SECONDS).max(MAX_ADMIN_GPU_BUCKET_SECONDS),
  capStreams: z.number().int().positive(),
  provider: z.literal('tensorx-dedicated'),
  totalCalls: z.number().int().nonnegative(),
  totalRateLimited429: z.number().int().nonnegative(),
  totalRateLimited429Priority: z.number().int().nonnegative(),
  totalRateLimited429Batch: z.number().int().nonnegative(),
  truncated: z.boolean(),
  /** Seuil de sortie sous lequel une requête est exclue des percentiles tk/s (rappelé dans l'infobulle). */
  tokensPerSecondMinOutputTokens: z.number().int().nonnegative(),
  buckets: z.array(GpuConcurrencyBucketSchema).max(MAX_ADMIN_TIME_RANGE_POINTS),
}).strict().superRefine((response, context) => {
  const end = response.toExclusive ?? response.to;
  if (!end) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['toExclusive'],
      message: 'GPU concurrency response must include toExclusive or legacy to',
    });
    return;
  }
  if (response.toExclusive && response.to && response.toExclusive !== response.to) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['to'],
      message: 'GPU concurrency response to must equal toExclusive during the transition',
    });
  }
  if (Date.parse(response.from) >= Date.parse(end)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['toExclusive'],
      message: 'GPU concurrency response end must be strictly after from',
    });
  }
});

export type AdminGpuConcurrencyQuery = z.infer<typeof AdminGpuConcurrencyQuerySchema>;
export type GpuTokensPerSecond = z.infer<typeof GpuTokensPerSecondSchema>;
export type GpuConcurrencyBucket = z.infer<typeof GpuConcurrencyBucketSchema>;
export type GpuConcurrencyResponse = z.infer<typeof GpuConcurrencyResponseSchema>;
